import { useState, useEffect, useCallback } from "react";
import apiv1 from "../api_v1";
import { fmtFechaEC } from "../utils/fecha";
import { useAuth } from "../contexts/AuthContext";
import { useConfirm } from "./ConfirmDialog";

const FORMAS_PAGO = [
  { value: "efectivo", label: "Efectivo" },
  { value: "cheque", label: "Cheque" },
  { value: "tarjeta_credito", label: "Tarjeta de crédito" },
  { value: "tarjeta_debito", label: "Tarjeta de débito" },
  { value: "transferencia", label: "Transferencia" },
  { value: "debito_automatico", label: "Débito automático" },
];
const CANALES_PAGO = [
  { value: "caja", label: "Caja" },
  { value: "ventanilla_banco", label: "Ventanilla banco" },
  { value: "debito_automatico", label: "Débito automático" },
  { value: "web", label: "Web" },
  { value: "app", label: "App" },
  { value: "agente", label: "Agente" },
];
const ESTADO_RECAUDO_BADGE = {
  pendiente:  "nv-badge-warn",
  confirmado: "nv-badge-ok",
  rechazado:  "nv-badge-err",
  reversado:  "nv-badge-muted",
  liquidado:  "nv-badge-info",
};

const fmtMoney = (v) =>
  v == null || isNaN(Number(v)) ? "—" : "$" + Number(v).toFixed(2);

// Derivar estado de factura (el backend no manda `status`).
const facturaEstado = (f) => {
  const pend = Number(f.pendiente || 0);
  const apl  = Number(f.aplicado  || 0);
  if (pend <= 0) return "pagada";
  if (apl > 0 && pend > 0) return "parcial";
  return "pendiente";
};

const ESTADO_BADGE = {
  pagada:    "nv-badge-ok",
  parcial:   "nv-badge-warn",
  pendiente: "nv-badge-err",
};

// Badge por estado de conciliación de una línea importada.
const IMPORT_BADGE = {
  matcheado:    "nv-badge-ok",
  rechazado:    "nv-badge-err",
  no_matcheado: "nv-badge-warn",
};

// Badge por estado de conciliación / upsert de una línea de facturado importado.
const FACTURADO_BADGE = {
  matcheado:         "nv-badge-ok",
  conciliado_manual: "nv-badge-ok",
  upserted:          "nv-badge-info",
  no_matcheado:      "nv-badge-warn",
};

// Estado de conciliación de una línea de facturado (el backend puede mandarlo
// como `estado_conciliacion` o `estado`).
const factLineaEstado = (l) => l.estado_conciliacion || l.estado || "no_matcheado";

export default function Recaudacion() {
  const { user } = useAuth();
  const isAdmin = ["admin", "superadmin"].includes(user?.role);

  const [clientes, setClientes] = useState([]);
  const [filtro,   setFiltro]   = useState("");
  const [cid,      setCid]      = useState("");
  const [cuenta,   setCuenta]   = useState(null);
  const [loading,  setLoading]  = useState(false);
  const [msg,      setMsg]      = useState(null);
  // window.confirm no abre cartel en el navegador y devuelve false: cancelaba solo.
  const [confirmar, dialogoConfirm] = useConfirm();

  // Form de registro de recaudo.
  const [formaPago,  setFormaPago]  = useState("efectivo");
  const [canalPago,  setCanalPago]  = useState("caja");
  const [monto,      setMonto]      = useState("");
  const [referencia, setReferencia] = useState("");
  const [notas,      setNotas]      = useState("");
  const [facturaSel, setFacturaSel] = useState("");   // factura a la que aplicar (vacío = FIFO)
  const [saving,     setSaving]     = useState(false);

  // Datos contables (banco / procesador) — todos opcionales.
  const [showContable, setShowContable] = useState(false);
  const [instRec,    setInstRec]    = useState("");
  const [numTx,      setNumTx]      = useState("");
  const [numComp,    setNumComp]    = useState("");
  const [comision,   setComision]   = useState("");
  const [retenciones, setRetenciones] = useState("");
  const [identExt,   setIdentExt]   = useState("");

  // Liquidación inline de un recaudo confirmado.
  const [liqId,    setLiqId]    = useState(null);   // id del recaudo en liquidación
  const [liqFecha, setLiqFecha] = useState("");
  const [liqEmp,   setLiqEmp]   = useState("");
  const [liqBusy,  setLiqBusy]  = useState(false);

  // Edición inline del N° de factura en el estado de cuenta.
  const [editFacId,  setEditFacId]  = useState(null);  // id de la factura en edición
  const [editFacVal, setEditFacVal] = useState("");    // valor del input
  const [editFacBusy, setEditFacBusy] = useState(false);

  // Admin: config + cortes propuestos.
  const [config,    setConfig]    = useState(null);
  const [propuestos, setPropuestos] = useState(null);
  const [savingCfg,  setSavingCfg]  = useState(false);
  const [loadingProp, setLoadingProp] = useState(false);

  // Navegación por pestañas.
  const [tab, setTab] = useState("recaudos");

  // Importar débitos.
  const [adapters,     setAdapters]     = useState([]);
  const [adapterClave, setAdapterClave] = useState("");
  const [importFile,   setImportFile]   = useState(null);
  const [preview,      setPreview]      = useState(null);
  const [previewing,   setPreviewing]   = useState(false);
  const [confirming,   setConfirming]   = useState(false);
  const [historial,    setHistorial]    = useState([]);
  const [asignando,    setAsignando]    = useState(null);   // id de línea en curso
  const [asignSel,     setAsignSel]     = useState({});     // { [lineaId]: client_id }

  // Facturas importadas (2b-B: import del facturado por planilla XLS).
  const [factFile,       setFactFile]       = useState(null);
  const [factPreview,    setFactPreview]    = useState(null);
  const [factPreviewing, setFactPreviewing] = useState(false);
  const [factConfirming, setFactConfirming] = useState(false);
  const [factAsignSel,   setFactAsignSel]   = useState({});   // { [lineaId]: client_id }
  const [factAsignando,  setFactAsignando]  = useState(null); // id de línea en curso
  const [factList,       setFactList]       = useState([]);   // facturas importadas (invoices)
  const [factBatches,    setFactBatches]    = useState([]);   // historial de lotes de facturado

  const showMsg = (text, type = "success") => {
    setMsg({ text, type });
    setTimeout(() => setMsg(null), 4000);
  };

  // --- Carga de clientes (selector) ---
  const cargarClientes = useCallback(async () => {
    try {
      const r = await apiv1.get("/onboarding/agent/list?limit=500");
      setClientes(r?.data?.data || []);
    } catch (e) {
      showMsg(e?.response?.data?.detail || "Error al cargar clientes", "error");
    }
  }, []);

  // --- Estado de cuenta del cliente ---
  const cargar = useCallback(async (id) => {
    if (!id) { setCuenta(null); return; }
    setLoading(true);
    try {
      const r = await apiv1.get(`/recaudacion/clientes/${id}/estado-cuenta`);
      setCuenta(r?.data || null);
    } catch (e) {
      setCuenta(null);
      showMsg(e?.response?.data?.detail || "Error al cargar estado de cuenta", "error");
    } finally {
      setLoading(false);
    }
  }, []);

  const cargarConfig = useCallback(async () => {
    try {
      const r = await apiv1.get("/recaudacion/config");
      setConfig(r?.data || null);
    } catch (e) {
      // 403 si el rol no alcanza: se ignora en UI.
    }
  }, []);

  useEffect(() => { cargarClientes(); }, [cargarClientes]);
  useEffect(() => { if (isAdmin) cargarConfig(); }, [isAdmin, cargarConfig]);

  const seleccionar = (id) => {
    setCid(id);
    setFacturaSel("");        // reset del selector de factura al cambiar de cliente
    setEditFacId(null);       // cerrar cualquier edición inline abierta
    cargar(id);
  };

  // --- Registrar recaudo + aplicar (FIFO) ---
  const registrar = async () => {
    if (!cid || Number(monto) <= 0) return;
    setSaving(true);
    try {
      const r = await apiv1.post("/recaudacion/recaudos", {
        client_id: cid,
        monto: Number(monto),
        forma_pago: formaPago,
        canal_pago: canalPago,
        referencia,
        notas,
        identificador_externo: identExt,
        institucion_recaudadora: instRec,
        numero_transaccion: numTx,
        numero_comprobante: numComp,
        comision: comision === "" ? null : Number(comision),
        retenciones: retenciones === "" ? null : Number(retenciones),
      });
      const rid = r?.data?.id;
      if (rid != null) {
        const aplicarBody = facturaSel ? { invoice_ids: [facturaSel] } : {};
        await apiv1.post(`/recaudacion/recaudos/${rid}/aplicar`, aplicarBody);
      }
      showMsg("Recaudo registrado y aplicado");
      setMonto(""); setReferencia(""); setNotas(""); setFacturaSel("");
      setInstRec(""); setNumTx(""); setNumComp(""); setComision(""); setRetenciones(""); setIdentExt("");
      cargar(cid);
    } catch (e) {
      showMsg(e?.response?.data?.detail || "Error al registrar recaudo", "error");
    } finally {
      setSaving(false);
    }
  };

  // --- Reversar recaudo ---
  const reversar = async (rid) => {
    if (!(await confirmar("¿Reversar este recaudo? Se generará un contra-movimiento."))) return;
    try {
      await apiv1.post(`/recaudacion/recaudos/${rid}/reversar`);
      showMsg("Recaudo reversado");
      cargar(cid);
    } catch (e) {
      showMsg(e?.response?.data?.detail || "Error al reversar", "error");
    }
  };

  // --- Liquidación inline de un recaudo confirmado ---
  const abrirLiquidacion = (r) => {
    setLiqId(r.id);
    setLiqFecha(r.fecha_liquidacion ? String(r.fecha_liquidacion).slice(0, 10) : "");
    setLiqEmp(r.empresa_procesadora || "");
  };
  const cancelarLiquidacion = () => {
    setLiqId(null);
    setLiqFecha("");
    setLiqEmp("");
  };
  const guardarLiquidacion = async (r) => {
    if (!liqFecha) { showMsg("Indicá la fecha de liquidación", "error"); return; }
    setLiqBusy(true);
    try {
      await apiv1.put(`/recaudacion/recaudos/${r.id}/liquidacion`, {
        fecha_liquidacion: liqFecha,
        empresa_procesadora: liqEmp,
      });
      cancelarLiquidacion();
      showMsg("Recaudo liquidado");
      cargar(cid);
    } catch (e) {
      showMsg(e?.response?.data?.detail || "Error al liquidar", "error");
    } finally {
      setLiqBusy(false);
    }
  };

  // --- Edición inline del N° de factura ---
  const abrirEdicionFactura = (f) => {
    setEditFacId(f.id);
    setEditFacVal(f.numero_factura || "");
  };
  const cancelarEdicionFactura = () => {
    setEditFacId(null);
    setEditFacVal("");
  };
  const guardarNumeroFactura = async (f) => {
    setEditFacBusy(true);
    try {
      // Permitimos vaciar el campo: '' → el backend lo deja en NULL.
      await apiv1.put(`/recaudacion/facturas/${f.id}/numero`, { numero_factura: editFacVal.trim() });
      cancelarEdicionFactura();
      await cargar(cid);
      showMsg("Número de factura actualizado");
    } catch (e) {
      showMsg(e?.response?.data?.detail || "Error", "error");
    } finally {
      setEditFacBusy(false);
    }
  };

  // --- Guardar config (admin) ---
  const setCfg = (k, v) => setConfig((c) => ({ ...(c || {}), [k]: v }));
  const guardarConfig = async () => {
    setSavingCfg(true);
    try {
      await apiv1.put("/recaudacion/config", config);
      showMsg("Configuración guardada");
    } catch (e) {
      showMsg(e?.response?.data?.detail || "Error al guardar configuración", "error");
    } finally {
      setSavingCfg(false);
    }
  };

  // --- Cortes propuestos (dry-run, admin) ---
  const verPropuestas = async () => {
    setLoadingProp(true);
    try {
      const r = await apiv1.get("/recaudacion/cortes/propuestos");
      setPropuestos(r?.data || null);
    } catch (e) {
      showMsg(e?.response?.data?.detail || "Error al obtener propuestas", "error");
    } finally {
      setLoadingProp(false);
    }
  };

  // ---------------------------------------------------------------------------
  // Importar débitos
  // ---------------------------------------------------------------------------

  // Normaliza las líneas del detalle de un lote al shape del preview.
  const normLineasDetalle = (lineas) =>
    (lineas || []).map((l) => ({
      id:           l.id,
      referencia:   l.referencia_banco,
      nombre:       l.nombre,
      num_id:       l.num_id,
      monto:        l.monto,
      estado_banco: l.estado_banco,
      motivo:       l.motivo,
      client_id:    l.client_id,
      estado:       l.estado_conciliacion,
    }));

  const cargarAdapters = useCallback(async () => {
    try {
      const r = await apiv1.get("/recaudacion/import/adapters");
      setAdapters(r?.data?.adapters || []);
    } catch (e) {
      showMsg(e?.response?.data?.detail || "Error al cargar adaptadores", "error");
    }
  }, []);

  const cargarHistorial = useCallback(async () => {
    try {
      const rh = await apiv1.get("/recaudacion/import/batches");
      setHistorial(rh?.data?.data || []);
    } catch (e) {
      showMsg(e?.response?.data?.detail || "Error al cargar historial", "error");
    }
  }, []);

  useEffect(() => {
    if (tab === "import") { cargarAdapters(); cargarHistorial(); }
  }, [tab, cargarAdapters, cargarHistorial]);

  // Subir archivo + previsualizar el lote (sin aplicar aún).
  const previsualizar = async () => {
    if (!importFile || !adapterClave) return;
    setPreviewing(true);
    try {
      const [banco, tipo] = adapterClave.split(":");
      const fd = new FormData();
      fd.append("banco", banco);
      fd.append("tipo", tipo);
      fd.append("file", importFile);
      // No seteamos Content-Type: axios arma el boundary del multipart solo.
      const r = await apiv1.post("/recaudacion/import/preview", fd);
      setPreview(r?.data || null);
      setAsignSel({});
    } catch (e) {
      // 409 = "ya fue importado" (el detalle lo explica).
      showMsg(e?.response?.data?.detail || "Error al previsualizar", "error");
    } finally {
      setPreviewing(false);
    }
  };

  // Conciliación manual de una línea no matcheada.
  const asignarLinea = async (linea) => {
    const client_id = asignSel[linea.id];
    if (!client_id || !preview?.batch_id) return;
    setAsignando(linea.id);
    try {
      await apiv1.put(`/recaudacion/import/lineas/${linea.id}/asignar`, { client_id });
      const rb = await apiv1.get(`/recaudacion/import/batches/${preview.batch_id}`);
      setPreview((p) => ({ ...(p || {}), lineas: normLineasDetalle(rb?.data?.lineas) }));
      showMsg("Línea conciliada");
    } catch (e) {
      showMsg(e?.response?.data?.detail || "Error al conciliar", "error");
    } finally {
      setAsignando(null);
    }
  };

  // Confirmar el lote: aplica cobros y deja pendientes los no conciliados.
  const confirmarImport = async () => {
    if (!preview?.batch_id) return;
    setConfirming(true);
    try {
      const r = await apiv1.post(`/recaudacion/import/${preview.batch_id}/confirmar`);
      showMsg(`Aplicados ${r?.data?.aplicadas}; sin conciliar ${r?.data?.no_matcheados_pendientes}`);
      setPreview(null);
      setImportFile(null);
      setAsignSel({});
      cargarHistorial();
    } catch (e) {
      showMsg(e?.response?.data?.detail || "Error al confirmar import", "error");
    } finally {
      setConfirming(false);
    }
  };

  // ---------------------------------------------------------------------------
  // Facturas importadas (2b-B)
  // ---------------------------------------------------------------------------

  const cargarFacturas = useCallback(async () => {
    try {
      const r = await apiv1.get("/recaudacion/facturado/facturas");
      // El endpoint devuelve la lista; toleramos varios shapes.
      setFactList(r?.data?.data || r?.data?.facturas || r?.data || []);
    } catch (e) {
      showMsg(e?.response?.data?.detail || "Error al cargar facturas importadas", "error");
    }
  }, []);

  const cargarFactBatches = useCallback(async () => {
    try {
      const r = await apiv1.get("/recaudacion/facturado/batches");
      setFactBatches(r?.data?.data || r?.data?.batches || r?.data || []);
    } catch (e) {
      showMsg(e?.response?.data?.detail || "Error al cargar historial de facturado", "error");
    }
  }, []);

  useEffect(() => {
    if (tab === "facturado") { cargarFacturas(); cargarFactBatches(); }
  }, [tab, cargarFacturas, cargarFactBatches]);

  // Descargar la plantilla .xlsx que define Netvoice.
  const descargarPlantilla = async () => {
    try {
      const r = await apiv1.get("/recaudacion/facturado/plantilla", { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = "plantilla_facturado.xlsx";
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      showMsg(e?.response?.data?.detail || "Error al descargar plantilla", "error");
    }
  };

  // Subir planilla + previsualizar el lote (sin escribir en invoices aún).
  const previsualizarFacturado = async () => {
    if (!factFile) return;
    setFactPreviewing(true);
    try {
      const fd = new FormData();
      fd.append("file", factFile);
      // No seteamos Content-Type: axios arma el boundary del multipart solo.
      const r = await apiv1.post("/recaudacion/facturado/preview", fd);
      setFactPreview(r?.data || null);
      setFactAsignSel({});
    } catch (e) {
      // 409 = "ya fue importado" / 400 = archivo ilegible o columnas faltantes.
      showMsg(e?.response?.data?.detail || "Error al previsualizar facturado", "error");
    } finally {
      setFactPreviewing(false);
    }
  };

  // Conciliación manual de una línea de facturado no matcheada.
  const asignarLineaFacturado = async (linea) => {
    const client_id = factAsignSel[linea.id];
    if (!client_id || !factPreview?.batch_id) return;
    setFactAsignando(linea.id);
    try {
      await apiv1.put(`/recaudacion/facturado/lineas/${linea.id}/asignar`, { client_id });
      // Refrescamos la línea en el preview (optimista) + recomputamos contadores.
      setFactPreview((p) => {
        if (!p) return p;
        const lineas = (p.lineas || []).map((l) =>
          l.id === linea.id
            ? { ...l, client_id, estado_conciliacion: "conciliado_manual", estado: "matcheado" }
            : l);
        const no_matcheados = lineas.filter((l) => factLineaEstado(l) === "no_matcheado").length;
        return { ...p, lineas, no_matcheados, matcheados: lineas.length - no_matcheados };
      });
      showMsg("Línea conciliada");
    } catch (e) {
      showMsg(e?.response?.data?.detail || "Error al conciliar", "error");
    } finally {
      setFactAsignando(null);
    }
  };

  // Confirmar el lote: upsert en invoices por numero_factura (solo admin).
  const confirmarFacturado = async () => {
    if (!factPreview?.batch_id) return;
    setFactConfirming(true);
    try {
      const r = await apiv1.post(`/recaudacion/facturado/${factPreview.batch_id}/confirmar`);
      showMsg(`Facturas: ${r?.data?.creados} creadas, ${r?.data?.actualizados} actualizadas; sin conciliar ${r?.data?.no_matcheados_pendientes}`);
      setFactPreview(null);
      setFactFile(null);
      setFactAsignSel({});
      cargarFacturas();
      cargarFactBatches();
    } catch (e) {
      showMsg(e?.response?.data?.detail || "Error al confirmar facturado", "error");
    } finally {
      setFactConfirming(false);
    }
  };

  // Nombre del cliente matcheado en una línea de preview (lookup en `clientes`).
  const nombreClienteLinea = (l) => {
    if (!l.client_id) return null;
    const c = clientes.find((x) => String(x.id) === String(l.client_id));
    return c?.name || l.cliente_nombre || l.nombre || "(cliente)";
  };

  // --- Derivados para KPIs ---
  const facturas = cuenta?.facturas || [];
  const recaudos = cuenta?.recaudos || [];
  const facturasPend = facturas.filter((f) => Number(f.pendiente || 0) > 0).length;
  const saldo = cuenta?.saldo_pendiente;

  // Busca por nombre, cédula/RUC, número de contrato, número de línea (DID) o teléfono.
  const clientesFiltrados = clientes.filter((c) => {
    const q = filtro.trim().toLowerCase();
    if (!q) return true;
    return [c.name, c.ruc, c.numero_contrato, c.did_number, c.phone]
      .some((v) => (v || "").toString().toLowerCase().includes(q));
  });
  const clienteSel = clientes.find((c) => String(c.id) === String(cid));

  const autoCorte = (config?.auto_corte || "no") === "yes";

  return (
    <div>
      {dialogoConfirm}
      <div className="nv-page-header">
        <div>
          <div className="nv-page-title">Recaudación · Caja &amp; Cobranza</div>
          <div className="nv-page-sub">Registro de cobros, conciliación de cartera y propuestas de corte</div>
        </div>
        <div className="nv-page-actions">
          <button className="nv-btn nv-btn-secondary nv-btn-sm"
                  onClick={() => { cargarClientes(); if (cid) cargar(cid); }}
                  disabled={loading}>
            {loading ? <span className="nv-spinner" /> : "↺"} Actualizar
          </button>
        </div>
      </div>

      {msg && (
        <div style={{
          marginBottom: 14, padding: "10px 14px", borderRadius: 8, fontSize: 13, fontWeight: 600,
          color: msg.type === "error" ? "var(--danger)" : "var(--success)",
          background: msg.type === "error" ? "rgba(239,68,68,.10)" : "rgba(34,197,94,.10)",
          border: "1px solid " + (msg.type === "error" ? "var(--danger)" : "var(--success)"),
        }}>
          {msg.text}
        </div>
      )}

      {/* Pestañas */}
      <div className="nv-tabs">
        <div className={"nv-tab" + (tab === "recaudos" ? " active" : "")}
             onClick={() => setTab("recaudos")}>Recaudos</div>
        <div className={"nv-tab" + (tab === "import" ? " active" : "")}
             onClick={() => setTab("import")}>Importar débitos</div>
        <div className={"nv-tab" + (tab === "facturado" ? " active" : "")}
             onClick={() => setTab("facturado")}>Facturas importadas</div>
      </div>

      {tab === "recaudos" && (
      <>
      {/* KPIs */}
      <div className="nv-kpi-grid" style={{ marginBottom: 16 }}>
        <div className="nv-kpi">
          <div className="nv-kpi-label">Saldo pendiente</div>
          <div className="nv-kpi-value" style={{ color: Number(saldo || 0) > 0 ? "var(--danger)" : "var(--success)" }}>
            {cid ? fmtMoney(saldo) : "—"}
          </div>
          <div className="nv-kpi-sub">{clienteSel?.name || "Sin cliente seleccionado"}</div>
        </div>
        <div className="nv-kpi">
          <div className="nv-kpi-label">Facturas pendientes</div>
          <div className="nv-kpi-value" style={{ color: "var(--warning)" }}>
            {cid ? facturasPend : "—"}
          </div>
          <div className="nv-kpi-sub">Con saldo por cobrar</div>
        </div>
        <div className="nv-kpi">
          <div className="nv-kpi-label">Recaudos (cliente)</div>
          <div className="nv-kpi-value" style={{ color: "var(--info)" }}>
            {cid ? recaudos.length : "—"}
          </div>
          <div className="nv-kpi-sub">Movimientos registrados</div>
        </div>
      </div>

      {/* Selector de cliente */}
      <div className="nv-card" style={{ marginBottom: 16 }}>
        <div className="nv-card-header">
          <span className="nv-card-title">👤 Cliente</span>
          <span style={{ fontSize: 11, color: "var(--text-muted)" }}>{clientes.length} clientes</span>
        </div>
        <div className="nv-form-row" style={{ padding: 12, gridTemplateColumns: "1fr 1fr", alignItems: "end" }}>
          <div className="nv-form-field">
            <label className="nv-label">Buscar (nombre, cédula/RUC, contrato o línea)</label>
            <input className="nv-input" value={filtro}
                   onChange={(e) => setFiltro(e.target.value)}
                   placeholder="ej: Acme · 0912345678 · CT-2026-0001 · 5934390…" />
          </div>
          <div className="nv-form-field">
            <label className="nv-label">Seleccionar cliente</label>
            <select className="nv-select" value={cid}
                    onChange={(e) => seleccionar(e.target.value)}>
              <option value="">— Elegir cliente —</option>
              {clientesFiltrados.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}{c.numero_contrato ? ` · ${c.numero_contrato}` : ""}{c.did_number ? ` · ${c.did_number}` : ""}{c.status ? ` (${c.status})` : ""}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      <div className="nv-grid-2">
        {/* Registrar recaudo */}
        <div className="nv-card">
          <div className="nv-card-header"><span className="nv-card-title">💵 Registrar recaudo</span></div>
          <div style={{ padding: 12 }}>
            <div className="nv-form-row" style={{ gridTemplateColumns: "1fr 1fr", marginBottom: 12 }}>
              <div className="nv-form-field">
                <label className="nv-label">Forma de pago</label>
                <select className="nv-select" value={formaPago} onChange={(e) => setFormaPago(e.target.value)}>
                  {FORMAS_PAGO.map((f) => <option key={f.value} value={f.value}>{f.label}</option>)}
                </select>
              </div>
              <div className="nv-form-field">
                <label className="nv-label">Canal de pago</label>
                <select className="nv-select" value={canalPago} onChange={(e) => setCanalPago(e.target.value)}>
                  {CANALES_PAGO.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
                </select>
              </div>
            </div>
            <div className="nv-form-field" style={{ marginBottom: 12 }}>
              <label className="nv-label">Monto</label>
              <input className="nv-input" type="number" step="0.01" min="0" value={monto}
                     onChange={(e) => setMonto(e.target.value)} placeholder="0.00" />
            </div>
            <div className="nv-form-field" style={{ marginBottom: 12 }}>
              <label className="nv-label">Referencia</label>
              <input className="nv-input" value={referencia}
                     onChange={(e) => setReferencia(e.target.value)}
                     placeholder="ej: Recibo #1024 / N° transferencia" />
            </div>
            <div className="nv-form-field" style={{ marginBottom: 12 }}>
              <label className="nv-label">Notas</label>
              <input className="nv-input" value={notas}
                     onChange={(e) => setNotas(e.target.value)} placeholder="Opcional" />
            </div>

            {/* Datos del banco / procesador (opcional) */}
            <div style={{ marginBottom: 12 }}>
              <button type="button" className="nv-btn nv-btn-secondary nv-btn-sm"
                      onClick={() => setShowContable((v) => !v)}>
                {showContable ? "− datos contables" : "＋ datos contables"}
              </button>
            </div>
            {showContable && (
              <div style={{ marginBottom: 12 }}>
                <div style={{ fontSize: 11, color: "var(--text-secondary)", marginBottom: 8, fontWeight: 600 }}>
                  Datos del banco / procesador (opcional)
                </div>
                <div className="nv-form-field" style={{ marginBottom: 10 }}>
                  <label className="nv-label">Institución recaudadora</label>
                  <input className="nv-input" value={instRec}
                         onChange={(e) => setInstRec(e.target.value)}
                         placeholder="ej: Banco Pichincha" />
                </div>
                <div className="nv-form-row" style={{ gridTemplateColumns: "1fr 1fr", marginBottom: 10 }}>
                  <div className="nv-form-field">
                    <label className="nv-label">N° transacción</label>
                    <input className="nv-input" value={numTx}
                           onChange={(e) => setNumTx(e.target.value)} placeholder="Opcional" />
                  </div>
                  <div className="nv-form-field">
                    <label className="nv-label">N° comprobante</label>
                    <input className="nv-input" value={numComp}
                           onChange={(e) => setNumComp(e.target.value)} placeholder="Opcional" />
                  </div>
                </div>
                <div className="nv-form-row" style={{ gridTemplateColumns: "1fr 1fr", marginBottom: 10 }}>
                  <div className="nv-form-field">
                    <label className="nv-label">Comisión</label>
                    <input className="nv-input" type="number" step="0.01" min="0" value={comision}
                           onChange={(e) => setComision(e.target.value)} placeholder="0.00" />
                  </div>
                  <div className="nv-form-field">
                    <label className="nv-label">Retenciones</label>
                    <input className="nv-input" type="number" step="0.01" min="0" value={retenciones}
                           onChange={(e) => setRetenciones(e.target.value)} placeholder="0.00" />
                  </div>
                </div>
                <div className="nv-form-field">
                  <label className="nv-label">Identificador externo</label>
                  <input className="nv-input" value={identExt}
                         onChange={(e) => setIdentExt(e.target.value)} placeholder="Opcional" />
                </div>
              </div>
            )}

            <div className="nv-form-field" style={{ marginBottom: 14 }}>
              <label className="nv-label">Factura</label>
              <select className="nv-select" value={facturaSel}
                      onChange={(e) => setFacturaSel(e.target.value)}>
                <option value="">— FIFO (todas las pendientes) —</option>
                {(cuenta?.facturas || [])
                  .filter((f) => Number(f.pendiente || 0) > 0)
                  .map((f) => (
                    <option key={f.id} value={f.id}>
                      {`${f.numero_factura || f.period || 'factura'} · saldo ${fmtMoney(f.pendiente)}`}
                    </option>
                  ))}
              </select>
            </div>
            <button className="nv-btn nv-btn-primary"
                    onClick={registrar}
                    disabled={saving || !cid || Number(monto) <= 0}>
              {saving ? <span className="nv-spinner" /> : "✓"} Registrar y aplicar
            </button>
            {!cid && (
              <div style={{ marginTop: 8, fontSize: 11, color: "var(--text-muted)" }}>
                Seleccioná un cliente para registrar un recaudo.
              </div>
            )}
          </div>
        </div>

        {/* Estado de cuenta */}
        <div className="nv-card">
          <div className="nv-card-header"><span className="nv-card-title">📄 Estado de cuenta</span></div>
          {!cid ? (
            <div style={{ textAlign: "center", padding: "36px 0", color: "var(--text-muted)", fontSize: 12 }}>
              Seleccioná un cliente para ver su cartera.
            </div>
          ) : loading ? (
            <div className="nv-loading"><span className="nv-spinner" /></div>
          ) : (
            <div style={{ padding: 12 }}>
              <div style={{ fontSize: 11, color: "var(--text-secondary)", marginBottom: 6, fontWeight: 600 }}>Facturas</div>
              {facturas.length ? (
                <div className="nv-table-wrap">
                  <table className="nv-table">
                    <thead>
                      <tr><th>N° Factura</th><th>Vence</th><th>Total</th><th>Aplicado</th><th>Pendiente</th><th>Estado</th></tr>
                    </thead>
                    <tbody>
                      {facturas.map((f) => {
                        const est = facturaEstado(f);
                        return (
                          <tr key={f.id}>
                            <td>
                              {editFacId === f.id ? (
                                <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
                                  <input className="nv-input" style={{ width: 120 }}
                                         value={editFacVal}
                                         onChange={(e) => setEditFacVal(e.target.value)}
                                         placeholder="N° Factura" autoFocus />
                                  <button className="nv-btn nv-btn-primary nv-btn-sm"
                                          onClick={() => guardarNumeroFactura(f)}
                                          disabled={editFacBusy}>
                                    {editFacBusy ? <span className="nv-spinner" /> : "Guardar"}
                                  </button>
                                  <button className="nv-btn nv-btn-secondary nv-btn-sm"
                                          onClick={cancelarEdicionFactura}
                                          disabled={editFacBusy}>
                                    Cancelar
                                  </button>
                                </div>
                              ) : (
                                <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
                                  <div>
                                    <div style={{ fontSize: 12 }}>{f.numero_factura || "—"}</div>
                                    {f.period && (
                                      <div style={{ fontSize: 10, color: "var(--text-muted)" }}>{f.period}</div>
                                    )}
                                  </div>
                                  <button className="nv-btn nv-btn-secondary nv-btn-sm"
                                          title="Editar N° de factura"
                                          onClick={() => abrirEdicionFactura(f)}>
                                    ✎
                                  </button>
                                </div>
                              )}
                            </td>
                            <td style={{ fontSize: 11 }}>{fmtFechaEC(f.due_date)}</td>
                            <td className="mono">{fmtMoney(f.total)}</td>
                            <td className="mono">{fmtMoney(f.aplicado)}</td>
                            <td className="mono" style={{ color: Number(f.pendiente || 0) > 0 ? "var(--danger)" : "var(--text-muted)" }}>
                              {fmtMoney(f.pendiente)}
                            </td>
                            <td><span className={"nv-badge " + (ESTADO_BADGE[est] || "nv-badge-muted")}>{est}</span></td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div style={{ textAlign: "center", padding: "16px 0", color: "var(--text-muted)", fontSize: 12 }}>Sin facturas</div>
              )}

              <div style={{ fontSize: 11, color: "var(--text-secondary)", margin: "14px 0 6px", fontWeight: 600 }}>Recaudos</div>
              {recaudos.length ? (
                <div className="nv-table-wrap">
                  <table className="nv-table">
                    <thead>
                      <tr><th>Fecha</th><th>Forma</th><th>Canal</th><th>N° Tx</th><th>N° Comp</th><th>Monto</th><th>Estado</th><th>Referencia</th><th></th></tr>
                    </thead>
                    <tbody>
                      {recaudos.map((r) => {
                        const comisionNum    = Number(r.comision || 0);
                        const retencionesNum = Number(r.retenciones || 0);
                        return (
                        <tr key={r.id}>
                          <td style={{ fontSize: 10 }}>{fmtFechaEC(r.fecha, { seconds: true })}</td>
                          <td style={{ fontSize: 11, color: "var(--text-muted)" }}>{r.forma_pago || r.canal}</td>
                          <td style={{ fontSize: 11, color: "var(--text-muted)" }}>{r.canal_pago || "—"}</td>
                          <td style={{ fontSize: 11, color: "var(--text-secondary)" }}>{r.numero_transaccion || "—"}</td>
                          <td style={{ fontSize: 11, color: "var(--text-secondary)" }}>{r.numero_comprobante || "—"}</td>
                          <td className="mono">
                            {fmtMoney(r.monto)}
                            {(comisionNum > 0 || retencionesNum > 0) && (
                              <div style={{ fontSize: 10, color: "var(--text-muted)" }}>
                                {comisionNum > 0 && <span>com. {fmtMoney(comisionNum)}</span>}
                                {comisionNum > 0 && retencionesNum > 0 && " · "}
                                {retencionesNum > 0 && <span>ret. {fmtMoney(retencionesNum)}</span>}
                              </div>
                            )}
                          </td>
                          <td>
                            <span className={"nv-badge " + (ESTADO_RECAUDO_BADGE[r.estado] || "nv-badge-muted")}>
                              {r.estado}
                            </span>
                          </td>
                          <td style={{ fontSize: 11, color: "var(--text-secondary)" }}>{r.referencia || "—"}</td>
                          <td>
                            {liqId === r.id ? (
                              <div style={{ display: "flex", gap: 6, alignItems: "center", flexWrap: "wrap" }}>
                                <input className="nv-input" type="date" style={{ width: 140 }}
                                       value={liqFecha} onChange={(e) => setLiqFecha(e.target.value)} />
                                <input className="nv-input" style={{ width: 150 }}
                                       value={liqEmp} onChange={(e) => setLiqEmp(e.target.value)}
                                       placeholder="Empresa procesadora (opc.)" />
                                <button className="nv-btn nv-btn-primary nv-btn-sm"
                                        onClick={() => guardarLiquidacion(r)} disabled={liqBusy}>
                                  {liqBusy ? <span className="nv-spinner" /> : "Guardar"}
                                </button>
                                <button className="nv-btn nv-btn-secondary nv-btn-sm"
                                        onClick={cancelarLiquidacion} disabled={liqBusy}>
                                  Cancelar
                                </button>
                              </div>
                            ) : r.estado === "confirmado" && (
                              <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
                                <button className="nv-btn nv-btn-secondary nv-btn-sm" onClick={() => abrirLiquidacion(r)}>
                                  Liquidar
                                </button>
                                <button className="nv-btn nv-btn-danger nv-btn-sm" onClick={() => reversar(r.id)}>
                                  Reversar
                                </button>
                              </div>
                            )}
                          </td>
                        </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div style={{ textAlign: "center", padding: "16px 0", color: "var(--text-muted)", fontSize: 12 }}>Sin recaudos</div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Config de corte (solo admin) */}
      {isAdmin && (
        <div className="nv-card" style={{ marginTop: 16 }}>
          <div className="nv-card-header">
            <span className="nv-card-title">⚙ Configuración de corte</span>
            <span className={"nv-badge " + (autoCorte ? "nv-badge-err" : "nv-badge-ok")}>
              {autoCorte ? "AUTO-CORTE ACTIVO" : "DRY-RUN (no corta)"}
            </span>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, padding: 12 }}>
            <div className="nv-form-field">
              <label className="nv-label">Días de gracia</label>
              <input className="nv-input" type="number" min="0" value={config?.dias_gracia ?? ""}
                     onChange={(e) => setCfg("dias_gracia", e.target.value)} />
            </div>
            <div className="nv-form-field">
              <label className="nv-label">Aviso (días antes)</label>
              <input className="nv-input" type="number" min="0" value={config?.aviso_dias_antes ?? ""}
                     onChange={(e) => setCfg("aviso_dias_antes", e.target.value)} />
            </div>
            <div className="nv-form-field">
              <label className="nv-label">Monto mínimo de corte</label>
              <input className="nv-input" type="number" step="0.01" min="0" value={config?.monto_minimo_corte ?? ""}
                     onChange={(e) => setCfg("monto_minimo_corte", e.target.value)} />
            </div>
            <div className="nv-form-field">
              <label className="nv-label">Auto-corte</label>
              <select className="nv-select" value={config?.auto_corte || "no"}
                      onChange={(e) => setCfg("auto_corte", e.target.value)}>
                <option value="no">No (dry-run)</option>
                <option value="yes">Sí (corta automáticamente)</option>
              </select>
            </div>
          </div>
          <div style={{ padding: "0 12px 12px" }}>
            <button className="nv-btn nv-btn-primary nv-btn-sm" onClick={guardarConfig} disabled={savingCfg || !config}>
              {savingCfg ? <span className="nv-spinner" /> : "Guardar"}
            </button>
          </div>
        </div>
      )}

      {/* Cortes propuestos (solo admin, dry-run) */}
      {isAdmin && (
        <div className="nv-card" style={{ marginTop: 16 }}>
          <div className="nv-card-header">
            <span className="nv-card-title">✂ Cortes propuestos (dry-run)</span>
            <span className={"nv-badge " + (autoCorte ? "nv-badge-err" : "nv-badge-ok")}>
              auto_corte: {config?.auto_corte || "no"}
            </span>
          </div>
          <div style={{ padding: 12 }}>
            <button className="nv-btn nv-btn-secondary nv-btn-sm" onClick={verPropuestas} disabled={loadingProp}>
              {loadingProp ? <span className="nv-spinner" /> : "Ver propuestas"}
            </button>
            {propuestos && (
              <div style={{ marginTop: 12 }}>
                <div style={{ fontSize: 11, color: "var(--text-muted)", marginBottom: 8 }}>
                  {propuestos.count ?? (propuestos.propuestos?.length || 0)} cliente(s) alcanzarían corte con la config actual.
                </div>
                {propuestos.propuestos?.length ? (
                  <div className="nv-table-wrap">
                    <table className="nv-table">
                      <thead><tr><th>Cliente</th><th>Monto vencido</th><th># Facturas</th></tr></thead>
                      <tbody>
                        {propuestos.propuestos.map((p) => (
                          <tr key={p.client_id}>
                            <td>{p.name}</td>
                            <td className="mono" style={{ color: "var(--danger)" }}>{fmtMoney(p.monto_vencido)}</td>
                            <td className="mono">{Array.isArray(p.facturas) ? p.facturas.length : (p.facturas ?? "—")}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div style={{ textAlign: "center", padding: "16px 0", color: "var(--text-muted)", fontSize: 12 }}>
                    Ningún cliente alcanza corte con la configuración actual.
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}
      </>
      )}

      {/* ================= TAB: Importar débitos ================= */}
      {tab === "import" && (
      <>
        {/* Origen del archivo */}
        <div className="nv-card" style={{ marginBottom: 16 }}>
          <div className="nv-card-header">
            <span className="nv-card-title">📥 Importar débitos bancarios</span>
            <span style={{ fontSize: 11, color: "var(--text-muted)" }}>{adapters.length} adaptador(es)</span>
          </div>
          <div className="nv-form-row" style={{ padding: 12 }}>
            <div className="nv-form-field">
              <label className="nv-label">Banco / formato</label>
              <select className="nv-select" value={adapterClave}
                      onChange={(e) => setAdapterClave(e.target.value)}>
                <option value="">— Elegir adaptador —</option>
                {adapters.map((a) => (
                  <option key={a.clave} value={a.clave}>{`${a.banco} / ${a.tipo}`}</option>
                ))}
              </select>
            </div>
            <div className="nv-form-field">
              <label className="nv-label">Archivo de débitos</label>
              <input className="nv-input" type="file" accept=".xls,.xlsx,.txt,.csv"
                     onChange={(e) => setImportFile(e.target.files?.[0] || null)} />
            </div>
          </div>
          <div style={{ padding: "0 12px 12px" }}>
            <button className="nv-btn nv-btn-primary"
                    onClick={previsualizar}
                    disabled={previewing || !importFile || !adapterClave}>
              {previewing ? <span className="nv-spinner" /> : "🔍"} Previsualizar
            </button>
          </div>
        </div>

        {/* Preview del lote */}
        {preview && (
          <div className="nv-card" style={{ marginBottom: 16 }}>
            <div className="nv-card-header">
              <span className="nv-card-title">📋 Previsualización del lote</span>
              <span style={{ fontSize: 11, color: "var(--text-muted)" }}>Lote #{preview.batch_id}</span>
            </div>
            <div className="nv-kpi-grid" style={{ padding: 12 }}>
              <div className="nv-kpi">
                <div className="nv-kpi-label">Total líneas</div>
                <div className="nv-kpi-value">{preview.total ?? "—"}</div>
              </div>
              <div className="nv-kpi">
                <div className="nv-kpi-label">Cobrados</div>
                <div className="nv-kpi-value" style={{ color: "var(--success)" }}>{preview.cobrados ?? "—"}</div>
              </div>
              <div className="nv-kpi">
                <div className="nv-kpi-label">Rechazados</div>
                <div className="nv-kpi-value" style={{ color: "var(--danger)" }}>{preview.rechazados ?? "—"}</div>
              </div>
              <div className="nv-kpi">
                <div className="nv-kpi-label">No matcheados</div>
                <div className="nv-kpi-value" style={{ color: "var(--warning)" }}>{preview.no_matcheados ?? "—"}</div>
              </div>
              <div className="nv-kpi">
                <div className="nv-kpi-label">Monto cobrado</div>
                <div className="nv-kpi-value" style={{ color: "var(--info)" }}>{fmtMoney(preview.monto_cobrado)}</div>
              </div>
            </div>

            <div className="nv-table-wrap">
              <table className="nv-table">
                <thead>
                  <tr><th>Nombre</th><th>Cédula/RUC</th><th>Monto</th><th>Estado</th><th>Motivo</th><th>Conciliar</th></tr>
                </thead>
                <tbody>
                  {(preview.lineas || []).map((l) => (
                    <tr key={l.id}>
                      <td>{l.nombre || "—"}</td>
                      <td className="mono">{l.num_id || "—"}</td>
                      <td className="mono">{fmtMoney(l.monto)}</td>
                      <td><span className={"nv-badge " + (IMPORT_BADGE[l.estado] || "nv-badge-muted")}>{l.estado}</span></td>
                      <td style={{ fontSize: 11, color: "var(--text-secondary)" }}>{l.motivo || "—"}</td>
                      <td>
                        {l.estado === "no_matcheado" ? (
                          <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
                            <select className="nv-select" style={{ minWidth: 150 }}
                                    value={asignSel[l.id] || ""}
                                    onChange={(e) => setAsignSel((s) => ({ ...s, [l.id]: e.target.value }))}>
                              <option value="">— Cliente —</option>
                              {clientes.map((c) => (
                                <option key={c.id} value={c.id}>{c.name}</option>
                              ))}
                            </select>
                            <button className="nv-btn nv-btn-secondary nv-btn-sm"
                                    onClick={() => asignarLinea(l)}
                                    disabled={asignando === l.id || !asignSel[l.id]}>
                              {asignando === l.id ? <span className="nv-spinner" /> : "Asignar"}
                            </button>
                          </div>
                        ) : (
                          <span style={{ fontSize: 11, color: "var(--text-muted)" }}>—</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {isAdmin && (
              <div style={{ padding: 12 }}>
                <button className="nv-btn nv-btn-primary"
                        onClick={confirmarImport}
                        disabled={confirming}>
                  {confirming ? <span className="nv-spinner" /> : "✓"} Confirmar import
                </button>
              </div>
            )}
          </div>
        )}

        {/* Historial de lotes */}
        <div className="nv-card">
          <div className="nv-card-header">
            <span className="nv-card-title">🗂 Historial de lotes</span>
            <button className="nv-btn nv-btn-secondary nv-btn-sm" onClick={cargarHistorial}>↺ Actualizar</button>
          </div>
          {historial.length ? (
            <div className="nv-table-wrap">
              <table className="nv-table">
                <thead>
                  <tr><th>Fecha</th><th>Banco</th><th>Archivo</th><th>Cobrados</th><th>Rechazados</th><th>No match.</th><th>Estado</th></tr>
                </thead>
                <tbody>
                  {historial.map((h, i) => (
                    <tr key={h.id ?? i}>
                      <td style={{ fontSize: 11 }}>{fmtFechaEC(h.created_at)}</td>
                      <td>{h.banco || "—"}</td>
                      <td style={{ fontSize: 11, color: "var(--text-secondary)" }}>{h.archivo_nombre || "—"}</td>
                      <td className="mono" style={{ color: "var(--success)" }}>{h.cobrados ?? "—"}</td>
                      <td className="mono" style={{ color: "var(--danger)" }}>{h.rechazados ?? "—"}</td>
                      <td className="mono" style={{ color: "var(--warning)" }}>{h.no_matcheados ?? "—"}</td>
                      <td><span className="nv-badge nv-badge-muted">{h.estado || "—"}</span></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div style={{ textAlign: "center", padding: "24px 0", color: "var(--text-muted)", fontSize: 12 }}>
              Sin lotes importados.
            </div>
          )}
        </div>
      </>
      )}

      {/* ================= TAB: Facturas importadas ================= */}
      {tab === "facturado" && (
      <>
        {/* Origen del archivo */}
        <div className="nv-card" style={{ marginBottom: 16 }}>
          <div className="nv-card-header">
            <span className="nv-card-title">🧾 Importar facturado (planilla)</span>
            <button className="nv-btn nv-btn-secondary nv-btn-sm" onClick={descargarPlantilla}>
              ⬇ Descargar plantilla
            </button>
          </div>
          <div className="nv-form-row" style={{ padding: 12 }}>
            <div className="nv-form-field">
              <label className="nv-label">Planilla de facturas (.xlsx / .xls / .csv)</label>
              <input className="nv-input" type="file" accept=".xlsx,.xls,.csv"
                     onChange={(e) => setFactFile(e.target.files?.[0] || null)} />
            </div>
          </div>
          <div style={{ padding: "0 12px 12px" }}>
            <button className="nv-btn nv-btn-primary"
                    onClick={previsualizarFacturado}
                    disabled={factPreviewing || !factFile}>
              {factPreviewing ? <span className="nv-spinner" /> : "🔍"} Previsualizar
            </button>
            <div style={{ marginTop: 8, fontSize: 11, color: "var(--text-muted)" }}>
              Descargá la plantilla, completá una fila por factura (N° factura, RUC, período, total,
              vencimiento) y subila. El match es por RUC (fallback por teléfono).
            </div>
          </div>
        </div>

        {/* Preview del lote */}
        {factPreview && (
          <div className="nv-card" style={{ marginBottom: 16 }}>
            <div className="nv-card-header">
              <span className="nv-card-title">📋 Previsualización del facturado</span>
              <span style={{ fontSize: 11, color: "var(--text-muted)" }}>Lote #{factPreview.batch_id}</span>
            </div>
            <div className="nv-kpi-grid" style={{ padding: 12 }}>
              <div className="nv-kpi">
                <div className="nv-kpi-label">Total líneas</div>
                <div className="nv-kpi-value">{factPreview.total ?? "—"}</div>
              </div>
              <div className="nv-kpi">
                <div className="nv-kpi-label">Matcheados</div>
                <div className="nv-kpi-value" style={{ color: "var(--success)" }}>{factPreview.matcheados ?? "—"}</div>
              </div>
              <div className="nv-kpi">
                <div className="nv-kpi-label">No matcheados</div>
                <div className="nv-kpi-value" style={{ color: "var(--warning)" }}>{factPreview.no_matcheados ?? "—"}</div>
              </div>
            </div>

            <div className="nv-table-wrap">
              <table className="nv-table">
                <thead>
                  <tr><th>N° Factura</th><th>RUC</th><th>Período</th><th>Total</th><th>Vencimiento</th><th>Cliente / estado</th></tr>
                </thead>
                <tbody>
                  {(factPreview.lineas || []).map((l) => {
                    const est = factLineaEstado(l);
                    const cli = nombreClienteLinea(l);
                    return (
                      <tr key={l.id}>
                        <td className="mono">{l.numero_factura || "—"}</td>
                        <td className="mono">{l.ruc || "—"}</td>
                        <td style={{ fontSize: 11 }}>{l.periodo || l.period || "—"}</td>
                        <td className="mono">{fmtMoney(l.total)}</td>
                        <td style={{ fontSize: 11 }}>{fmtFechaEC(l.vencimiento || l.due_date)}</td>
                        <td>
                          {est === "no_matcheado" ? (
                            <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
                              <select className="nv-select" style={{ minWidth: 150 }}
                                      value={factAsignSel[l.id] || ""}
                                      onChange={(e) => setFactAsignSel((s) => ({ ...s, [l.id]: e.target.value }))}>
                                <option value="">— Cliente —</option>
                                {clientes.map((c) => (
                                  <option key={c.id} value={c.id}>{c.name}</option>
                                ))}
                              </select>
                              <button className="nv-btn nv-btn-secondary nv-btn-sm"
                                      onClick={() => asignarLineaFacturado(l)}
                                      disabled={factAsignando === l.id || !factAsignSel[l.id]}>
                                {factAsignando === l.id ? <span className="nv-spinner" /> : "Asignar"}
                              </button>
                            </div>
                          ) : (
                            <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
                              <span className={"nv-badge " + (FACTURADO_BADGE[est] || "nv-badge-muted")}>{est}</span>
                              {cli && <span style={{ fontSize: 11, color: "var(--text-secondary)" }}>{cli}</span>}
                            </div>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {isAdmin && (
              <div style={{ padding: 12 }}>
                <button className="nv-btn nv-btn-primary"
                        onClick={confirmarFacturado}
                        disabled={factConfirming}>
                  {factConfirming ? <span className="nv-spinner" /> : "✓"} Confirmar import
                </button>
              </div>
            )}
          </div>
        )}

        {/* Listado de facturas importadas */}
        <div className="nv-card" style={{ marginBottom: 16 }}>
          <div className="nv-card-header">
            <span className="nv-card-title">📄 Facturas importadas</span>
            <button className="nv-btn nv-btn-secondary nv-btn-sm" onClick={cargarFacturas}>↺ Actualizar</button>
          </div>
          {factList.length ? (
            <div className="nv-table-wrap">
              <table className="nv-table">
                <thead>
                  <tr><th>N° Factura</th><th>Cliente</th><th>Período</th><th>Total</th><th>Vencimiento</th><th>Estado</th></tr>
                </thead>
                <tbody>
                  {factList.map((f, i) => {
                    const est = f.status || f.estado || "—";
                    return (
                      <tr key={f.id ?? f.invoice_id ?? i}>
                        <td className="mono">{f.numero_factura || "—"}</td>
                        <td>{f.client_name || f.name || f.cliente_nombre || f.cliente || "—"}</td>
                        <td style={{ fontSize: 11 }}>{f.period || f.periodo || "—"}</td>
                        <td className="mono">{fmtMoney(f.total)}</td>
                        <td style={{ fontSize: 11 }}>{fmtFechaEC(f.due_date || f.vencimiento)}</td>
                        <td><span className={"nv-badge " + (ESTADO_RECAUDO_BADGE[est] || "nv-badge-muted")}>{est}</span></td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ) : (
            <div style={{ textAlign: "center", padding: "24px 0", color: "var(--text-muted)", fontSize: 12 }}>
              Sin facturas importadas.
            </div>
          )}
        </div>

        {/* Historial de lotes de facturado */}
        <div className="nv-card">
          <div className="nv-card-header">
            <span className="nv-card-title">🗂 Historial de lotes</span>
            <button className="nv-btn nv-btn-secondary nv-btn-sm" onClick={cargarFactBatches}>↺ Actualizar</button>
          </div>
          {factBatches.length ? (
            <div className="nv-table-wrap">
              <table className="nv-table">
                <thead>
                  <tr><th>Fecha</th><th>Archivo</th><th>Total</th><th>Creadas</th><th>Actualizadas</th><th>No match.</th><th>Estado</th></tr>
                </thead>
                <tbody>
                  {factBatches.map((b, i) => (
                    <tr key={b.id ?? i}>
                      <td style={{ fontSize: 11 }}>{fmtFechaEC(b.created_at || b.confirmed_at)}</td>
                      <td style={{ fontSize: 11, color: "var(--text-secondary)" }}>{b.archivo_nombre || b.nombre_archivo || "—"}</td>
                      <td className="mono">{b.total ?? "—"}</td>
                      <td className="mono" style={{ color: "var(--success)" }}>{b.creados ?? "—"}</td>
                      <td className="mono" style={{ color: "var(--info)" }}>{b.actualizados ?? "—"}</td>
                      <td className="mono" style={{ color: "var(--warning)" }}>{b.no_matcheados ?? "—"}</td>
                      <td><span className="nv-badge nv-badge-muted">{b.estado || "—"}</span></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div style={{ textAlign: "center", padding: "24px 0", color: "var(--text-muted)", fontSize: 12 }}>
              Sin lotes de facturado importados.
            </div>
          )}
        </div>
      </>
      )}
    </div>
  );
}
